/**
 * Degenerate-geometry branches for the D-shape and horseshoe traces.
 *
 * The main plotting routine divides by TanAlpha and TanBeta. Every case where
 * one of those is 0 (or, for a vertical plane, infinite) is solved here
 * analytically in the tunnel cross-section instead:
 *
 *   SUBROUTINE-ALPHA0 (horizontal tunnel)   alphaZeroCrossSection / solveAlphaZero
 *   Horizontal plane, Amt = 0               solveLevel
 *   Vertical plane, Amt = 90, Alpha = 0     solveVertical
 *   SUBROUTINE-ALPHA0-INCLINED (I > 0)      alphaZeroInclined
 *   SUBROUTINE-PARALLEL-TRACE (Beta' = 0)   solveParallelTrace
 *
 * (Algorithm_d shape algorithm_rev.3_27Aug26 and Algorithm_Borderline
 * Cases_01Sep26.)
 *
 * Coordinates (spec A0-1):
 *   X — transverse, positive towards the Right Side looking in drive direction
 *   Z — height above the invert, in the section perpendicular to the axis
 *
 * geom describes either section: W at the invert, WS at the SPL (equal for the
 * D-shape, wider for the horseshoe, whose walls are battered), HW the wall
 * height and HWincl the wall's unfolded length — the slant for a battered
 * wall, HW itself when absent. Ylog is measured along the unfolded log from
 * the right invert, so a wall point's Ylog is its slant distance, not Z.
 *
 * Everything here is in metres and free of rendering concerns, so it can be
 * tested directly against the spec's own boundary-case tables.
 */

const toRadians = (angle) => angle * (Math.PI / 180);
const toDegree = (radian) => radian * (180 / Math.PI);

/** Spec A0-9: two surface equations can return the same physical point. */
export const GEOMETRIC_TOLERANCE = 0.001;

/**
 * A corrected apparent dip (Beta') this close to 0° is treated as exactly
 * parallel to the axis. Whole-degree inputs land either exactly on 0 or
 * whole degrees away from it, so the tolerance only absorbs float residue.
 */
export const BETA_ZERO_TOLERANCE = 1e-6;

/** Spec A0-11 — the exact wording required on an invert-interior trace. */
export const ALPHA0_INVERT_ALERT =
  "One discontinuity trace lies inside the invert and, hence, not plotted.";

/** Borderline Cases §1.1.2 — strike parallel, dipping plane. */
export const ALPHA0_PARALLEL_NOTE =
  "Discontinuity strike is parallel to the tunnel direction. The trace runs " +
  "parallel to the tunnel for unlimited length. Has been clipped at the log ends.";

/** Shown as soon as Dip Direction makes the strike parallel to the tunnel. */
export const ALPHA0_STRIKE_NOTE =
  "Discontinuity strike is parallel to the tunnel direction. The trace runs " +
  "parallel to the tunnel for unlimited length.";

/** "Discontinuity trace coincides with Right SPL and Left Invert." */
export const coincidenceAlert = (lines) =>
  `Discontinuity trace coincides with ${lines.join(" and ")}.`;

/** A horizontal plane through a point that is not on one of the log's lines. */
export const FLAT_PARALLEL_NOTE =
  "Discontinuity plane is horizontal. The trace runs parallel to the tunnel " +
  "for unlimited length. Has been clipped at the log ends.";

/** A horizontal plane in an inclined tunnel: one inclined trace. */
export const FLAT_INCLINED_ALERT =
  "Discontinuity plane horizontal. Trace inclined in inclined tunnel";

/** Corrected Beta = 0 in an inclined tunnel: the plane contains the axis direction. */
export const AXIS_PARALLEL_NOTE =
  "Discontinuity plane is parallel to the tunnel axis. The trace runs " +
  "parallel to the tunnel for unlimited length. Has been clipped at the log ends.";

/** Borderline Cases §1.1.1 / §1.1.4 — trace lies on one of the log's own lines. */
export const INVISIBLE_INVERT_ALERT =
  "Discontinuity trace coincides with left and right invert lines and, hence, " +
  "runs along them.";
export const INVISIBLE_SPL_ALERT =
  "Discontinuity trace coincides with left and right SPL lines and, hence, " +
  "runs along them.";
export const INVISIBLE_CROWN_ALERT =
  "Discontinuity trace coincides with Centre Line at Crown and, hence, " +
  "runs along it.";

/**
 * Borderline Cases §1.1.3 — the plane IS a wall: a vertical strike-parallel
 * plane for vertical walls, or one dipping at the batter for a horseshoe.
 */
export const VERTICAL_LEFT_WALL_ALERT =
  "Discontinuity Plane coincides with Left Wall. No trace seen.";
export const VERTICAL_RIGHT_WALL_ALERT =
  "Discontinuity Plane coincides with Right Wall. No trace seen.";
export const VERTICAL_CENTRELINE_ALERT =
  "Discontinuity Plane coincides with Centre line of the tunnel. " +
  "Trace runs along centre line at crown. " + ALPHA0_INVERT_ALERT;

/**
 * Angle between strike and tunnel direction, matching calculateAlpha in the
 * renderers and alphaBetween in horizontalTrace so all three agree on when
 * this branch applies.
 */
export const alphaBetween = (D, Dir) => {
  const Dnew = D > 180 ? D - 180 : D;
  const Dirnew = Dir > 180 ? Dir - 180 : Dir;
  const Value = Math.abs(Dnew - Dirnew);
  return Value <= 90 ? 90 - Value : Value - 90;
};

const wrap360 = (v) => ((v % 360) + 360) % 360;

/**
 * Spec A0-2 — which way the discontinuity dips across the tunnel.
 *
 * With Alpha = 0 the dip direction is perpendicular to the tunnel, so it is
 * either D+90 or D-90 exactly; inputs are whole degrees, so this is an exact
 * comparison rather than a tolerance.
 *
 * Returns -1 for dip towards the Right Side, +1 towards the Left, or null if
 * the strike is not actually parallel (caller should not have routed here).
 */
export function transverseSign(D, Dir) {
  const dir = wrap360(Dir);
  if (dir === wrap360(D + 90)) return -1;
  if (dir === wrap360(D - 90)) return 1;
  return null;
}

/**
 * Spec PT-2 — transverse dip sense for a plane whose strike is NOT parallel:
 * whichever of D+90 / D-90 the dip direction is nearer to. A tie (dip along
 * the axis, Alpha = 90) has no transverse component, so the sign is moot and
 * Right is returned by convention.
 */
export function parallelSign(D, Dir) {
  const diff = (p, q) => {
    const d = Math.abs(wrap360(p) - wrap360(q));
    return d > 180 ? 360 - d : d;
  };
  return diff(Dir, D - 90) < diff(Dir, D + 90) ? 1 : -1;
}

/**
 * Spec A0-1 — convert whichever input option was used into the common
 * cross-section coordinates (Xp, Hp).
 *
 * `side` and `d`/`Hp` are only consulted for the P6 ("Any Other Point") case.
 * Returns null for an unrecognised location.
 */
export function crossSectionPoint(orientation, geom, p6 = {}) {
  const { W, WS, HW, H } = geom;
  switch (orientation) {
    case "rightinvert": // P1
      return { Xp: W / 2, Hp: 0 };
    case "rightspl": // P2
      return { Xp: WS / 2, Hp: HW };
    case "leftinvert": // P3
      return { Xp: -W / 2, Hp: 0 };
    case "leftspl": // P4
      return { Xp: -WS / 2, Hp: HW };
    case "centerline": // P5
      return { Xp: 0, Hp: H };
    case "otherpoint": {
      // P6 — "If Point is on Centre Line, choose Right Side" (main algorithm),
      // so 'center' behaves as a zero offset rather than its own case.
      const d = Number(p6.d) || 0;
      const Hp = Number(p6.Hp) || 0;
      if (p6.side === "left") return { Xp: -d, Hp };
      return { Xp: d, Hp };
    }
    default:
      return null;
  }
}

const near = (a, b, tol = GEOMETRIC_TOLERANCE) => Math.abs(a - b) <= tol;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/** Unfolded length of one wall: the slant for a battered wall. */
export const wallLength = (geom) => (geom.HWincl > 0 ? geom.HWincl : geom.HW);

/** Ylog of a right-wall point Z above the invert (left wall mirrors it). */
export const wallYlog = (Z, geom) => (geom.HW > 0 ? (Z / geom.HW) * wallLength(geom) : 0);

/** Wall taper: X grows by b per metre of height; 0 for vertical walls. */
const wallTaper = ({ W, WS, HW }) => (HW > 0 ? (WS - W) / 2 / HW : 0);

/** Spec A0-7 — unfold a crown point onto the log. */
export function crownYlog(Xc, Zc, geom) {
  const { R, HWn, Arc, ArcFull, Theta } = geom;
  const HL = wallLength(geom);
  const n = toDegree(Math.asin(clamp((Zc - HWn) / R, -1, 1)));
  const span = 90 - Theta;
  const frac = span === 0 ? 0 : (n - Theta) / span;
  return Xc >= 0 ? HL + Arc * frac : HL + ArcFull - Arc * frac;
}

/**
 * Ylog of a face point that lies on the log's own surface — a wall or the
 * crown — or null for a point inside the face or on the invert floor, which
 * the unfolded log does not show. Points are typed to two decimals, so the
 * tolerance is a centimetre rather than the solver's millimetre.
 */
export function perimeterYlog(X, Z, geom, tol = 0.01) {
  const { W, WS, HW, R, HWn, ArcFull } = geom;
  const a = W / 2;
  const b = wallTaper(geom);
  const HL = wallLength(geom);
  if (Z >= -tol && Z <= HW + tol) {
    const z = clamp(Z, 0, HW);
    if (near(X, a + b * z, tol)) return wallYlog(z, geom);
    if (near(X, -a - b * z, tol)) return 2 * HL + ArcFull - wallYlog(z, geom);
  }
  if (Z >= HW - tol && Math.abs(X) <= WS / 2 + tol && near(Math.hypot(X, Z - HWn), R, tol)) {
    return crownYlog(X, Math.max(Z, HW), geom);
  }
  return null;
}

/**
 * The line Z = mX + c lies along a wall when it passes through both ends of
 * that wall. Returns "rightwall", "leftwall" or null.
 */
export function coincidentWall(m, c, geom) {
  const { W, WS, HW } = geom;
  const on = (X, Z) => near(m * X + c, Z);
  if (on(W / 2, 0) && on(WS / 2, HW)) return "rightwall";
  if (on(-W / 2, 0) && on(-WS / 2, HW)) return "leftwall";
  return null;
}
export const wallAlert = (wall) =>
  wall === "rightwall" ? VERTICAL_RIGHT_WALL_ALERT : wall === "leftwall" ? VERTICAL_LEFT_WALL_ALERT : null;

/**
 * Spec A0-3 to A0-9 — intersect the straight line Z = mX + c through
 * (Xp, Hp) with the tunnel perimeter. Shared by every branch whose trace is
 * a straight line in the cross-section; only the slope m differs.
 *
 * geom: { W, WS, HW, H, R, HWn, Arc, ArcFull, Theta } in metres/degrees, using
 * exactly the base computations the main algorithm already performs.
 *
 * Returns:
 *   traces          — unique intersections that the unfolded log can represent,
 *                     each { surface, X, Z, Ylog }, sorted by Ylog
 *   invertInterior  — true when a second intersection falls strictly inside the
 *                     invert floor (spec A0-11: alert, and do not plot it)
 *   wall            — "rightwall" / "leftwall" when the line IS that wall (the
 *                     plane coincides with it: nothing to draw, only the alert)
 *   line            — { m, c } of the cross-section line, for debugging/tests
 */
export function crossSectionTraces({ Xp, Hp, m, geom }) {
  const { W, WS, HW, R, HWn } = geom;

  // A0-3 — the discontinuity as a line in the cross-section
  const c = Hp - m * Xp;

  // Wall taper: b = 0 when the invert and SPL widths are equal (vertical walls)
  const a = W / 2;
  const b = wallTaper(geom);
  const HL = wallLength(geom);

  const wall = coincidentWall(m, c, geom);
  if (wall) {
    return { traces: [], invertInterior: false, invertX: null, wall, line: { m, c } };
  }

  const hits = [];

  // A0-4 — right wall, X = a + bZ
  const denomR = 1 - m * b;
  if (Math.abs(denomR) > Number.EPSILON) {
    const Zr = (Hp + m * (a - Xp)) / denomR;
    if (Zr >= -GEOMETRIC_TOLERANCE && Zr <= HW + GEOMETRIC_TOLERANCE) {
      const Z = clamp(Zr, 0, HW);
      hits.push({ surface: "rightwall", X: a + b * Z, Z, Ylog: wallYlog(Z, geom) });
    }
  }

  // A0-5 — left wall, X = -a - bZ
  const denomL = 1 + m * b;
  if (Math.abs(denomL) > Number.EPSILON) {
    const Zl = (Hp + m * (-a - Xp)) / denomL;
    if (Zl >= -GEOMETRIC_TOLERANCE && Zl <= HW + GEOMETRIC_TOLERANCE) {
      const Z = clamp(Zl, 0, HW);
      hits.push({ surface: "leftwall", X: -a - b * Z, Z, Ylog: 2 * HL + geom.ArcFull - wallYlog(Z, geom) });
    }
  }

  // A0-6 — crown, X² + (Z - HWn)² = R², with Z = mX + c
  const A = 1 + m * m;
  const B = 2 * m * (c - HWn);
  const C = (c - HWn) * (c - HWn) - R * R;
  const disc = B * B - 4 * A * C;
  if (disc >= 0) {
    const sq = Math.sqrt(disc);
    [(-B + sq) / (2 * A), (-B - sq) / (2 * A)].forEach((Xc) => {
      const Zc = m * Xc + c;
      if (Zc < HW - GEOMETRIC_TOLERANCE) return;
      if (Math.abs(Xc) > WS / 2 + GEOMETRIC_TOLERANCE) return;
      hits.push({ surface: "crown", X: Xc, Z: Zc, Ylog: crownYlog(Xc, Zc, geom) });
    });
  }

  // A0-8 — invert, Z = 0 (a horizontal line never reaches it)
  let invertInterior = false;
  let invertX = null;
  if (Math.abs(m) > Number.EPSILON) {
    const Xi = -c / m;
    if (Xi >= -a - GEOMETRIC_TOLERANCE && Xi <= a + GEOMETRIC_TOLERANCE) {
      invertX = Xi;
      // At exactly ±W/2 this is the invert *edge* — the same physical point the
      // wall solve already returned, so it is a duplicate, not a new trace.
      if (!near(Math.abs(Xi), a)) invertInterior = true;
    }
  }

  // A0-9 — drop duplicates: adjoining surfaces meet at P1..P4 and both report it
  const traces = [];
  hits.forEach((h) => {
    const dup = traces.find((t) => near(t.X, h.X) && near(t.Z, h.Z));
    if (!dup) traces.push(h);
  });
  traces.sort((p, q) => p.Ylog - q.Ylog);

  return { traces, invertInterior, invertX, wall: null, line: { m, c } };
}

/** The log line a section point lies on, or null for any other point. */
function sectionLineName(X, Z, { W, WS, HW, H }) {
  if (near(X, WS / 2) && near(Z, HW)) return "Right SPL";
  if (near(X, -WS / 2) && near(Z, HW)) return "Left SPL";
  if (near(X, W / 2) && near(Z, 0)) return "Right Invert";
  if (near(X, -W / 2) && near(Z, 0)) return "Left Invert";
  if (near(X, 0) && near(Z, H)) return "Centreline at Crown";
  return null;
}

/**
 * When both crossings of a strike-parallel trace fall on log lines, the trace
 * runs along those lines. Returns their names, the data point's line first,
 * or null.
 */
export function coincidentLines(traces, { Xp, Hp }, geom) {
  if (traces.length !== 2) return null;
  const ordered = [...traces].sort(
    (p, q) => Math.hypot(p.X - Xp, p.Z - Hp) - Math.hypot(q.X - Xp, q.Z - Hp)
  );
  const names = ordered.map((t) => sectionLineName(t.X, t.Z, geom));
  return names.every(Boolean) ? names : null;
}

/** SUBROUTINE-ALPHA0 proper: slope from the true dip, Beta = 0. */
export function alphaZeroCrossSection({ Xp, Hp, S, amt, geom }) {
  return crossSectionTraces({ Xp, Hp, m: S * Math.tan(toRadians(amt)), geom });
}

/**
 * Convenience wrapper: everything needed to plot one Alpha = 0 discontinuity.
 *
 * Returns null when the inputs do not describe an Alpha = 0 case, so the caller
 * can fall through to the main routine unchanged.
 */
export function solveAlphaZero({ D, Dir, amt, orientation, geom, p6 }) {
  const S = transverseSign(D, Dir);
  if (S === null) return null;
  const start = crossSectionPoint(orientation, geom, p6);
  if (!start) return null;
  const result = alphaZeroCrossSection({ Xp: start.Xp, Hp: start.Hp, S, amt, geom });
  const lines = result.wall ? null : coincidentLines(result.traces, start, geom);
  return { ...result, S, alert: wallAlert(result.wall), lines, ...start };
}

/**
 * A horizontal line in the cross-section at height Hp lies ON one of the
 * log's own lines when Hp is the invert (both invert lines), the wall height
 * (both SPL lines) or the tunnel height (tangent along the crown centreline).
 * Such a trace is drawn nowhere because it would be indistinguishable from
 * the log; the alert is the output instead.
 */
export function levelCoincidence(Hp, geom) {
  if (near(Hp, 0)) return INVISIBLE_INVERT_ALERT;
  if (near(Hp, geom.HW)) return INVISIBLE_SPL_ALERT;
  if (near(Hp, geom.H)) return INVISIBLE_CROWN_ALERT;
  return null;
}

/**
 * Horizontal plane (Amt = 0) in a horizontal tunnel. The strike is undefined,
 * so this does not depend on the dip direction at all.
 */
export function solveLevel({ orientation, geom, p6 }) {
  const start = crossSectionPoint(orientation, geom, p6);
  if (!start) return null;
  const result = crossSectionTraces({ Xp: start.Xp, Hp: start.Hp, m: 0, geom });
  return { ...result, alert: levelCoincidence(start.Hp, geom), ...start };
}

/**
 * Vertical plane with strike parallel to the tunnel (Amt = 90, Alpha = 0),
 * in a horizontal or inclined tunnel — either way the plane contains the axis
 * direction, so every intersection runs the full length of the log.
 *
 * Solved as the line X = Xp rather than Z = mX + c: tan 90° is a finite
 * 1.6e16 in floating point, which turns the crown quadratic into noise.
 *
 * For vertical walls, at |Xp| = W/2 the plane IS the wall — nothing to draw.
 * Battered walls instead have individual invert/SPL boundary coincidences.
 * Elsewhere it cuts the crown once (Ylog constant) and the invert floor at
 * X = Xp, which the log cannot show (A0-11).
 */
export function verticalCrossSection({ Xp, geom }) {
  const { W, WS, HW, R, HWn, ArcFull } = geom;
  const a = W / 2;
  const b = wallTaper(geom);
  const HL = wallLength(geom);
  // Vertical walls: the plane through their foot IS the wall.
  if (b === 0 && near(Xp, a)) {
    return { alert: VERTICAL_RIGHT_WALL_ALERT, wall: "rightwall", traces: [], invertInterior: false, invertX: null };
  }
  if (b === 0 && near(Xp, -a)) {
    return { alert: VERTICAL_LEFT_WALL_ALERT, wall: "leftwall", traces: [], invertInterior: false, invertX: null };
  }
  const traces = [];
  if (Math.abs(Xp) <= WS / 2 + GEOMETRIC_TOLERANCE) {
    const Zc = HWn + Math.sqrt(Math.max(0, R * R - Xp * Xp));
    traces.push({ surface: "crown", X: Xp, Z: Zc, Ylog: crownYlog(Xp, Zc, geom) });
  }
  // Battered walls lean outwards from the invert, so a vertical plane wider
  // than the invert but inside the SPL cuts the wall once on its way up.
  if (b > 0 && Math.abs(Xp) > a + GEOMETRIC_TOLERANCE && Math.abs(Xp) <= WS / 2 + GEOMETRIC_TOLERANCE) {
    const Z = clamp((Math.abs(Xp) - a) / b, 0, HW);
    if (Xp > 0) traces.push({ surface: "rightwall", X: Xp, Z, Ylog: wallYlog(Z, geom) });
    else traces.push({ surface: "leftwall", X: Xp, Z, Ylog: 2 * HL + ArcFull - wallYlog(Z, geom) });
  }
  traces.sort((p, q) => p.Ylog - q.Ylog);
  const inside = Math.abs(Xp) < a - GEOMETRIC_TOLERANCE;
  let alert = near(Xp, 0) ? VERTICAL_CENTRELINE_ALERT : null;
  if (b > 0 && !alert) {
    const side = Xp < 0 ? "left" : "right";
    const boundary = near(Math.abs(Xp), a)
      ? (Xp < 0 ? "invert line" : "invert")
      : near(Math.abs(Xp), WS / 2) ? "SPL" : null;
    if (boundary) {
      alert = `${ALPHA0_PARALLEL_NOTE} One trace coincides with the ${side} ${boundary}.`;
    }
  }
  return {
    alert,
    wall: null,
    traces,
    invertInterior: inside,
    invertX: inside ? Xp : null,
  };
}

export function solveVertical({ orientation, geom, p6 }) {
  const start = crossSectionPoint(orientation, geom, p6);
  if (!start) return null;
  return { ...verticalCrossSection({ Xp: start.Xp, geom }), ...start };
}

/**
 * SUBROUTINE-PARALLEL-TRACE — Alpha > 0 in an inclined tunnel, with the
 * apparent dip corrected for inclination coming out at 0: the plane contains
 * the axis direction, so its trace is again a straight line in the section
 * (slope PT-3) whose intersections run the whole log.
 *
 * With Alpha = 90 the slope is 0 and the line is level, which is Borderline
 * Cases §1.1.4 (the invert-parallel plane): through P1/P3, P2/P4 or P5 it
 * coincides with the log's lines and only the alert is shown.
 */
export function solveParallelTrace({ D, Dir, amt, alpha, I, orientation, geom, p6 }) {
  const start = crossSectionPoint(orientation, geom, p6);
  if (!start) return null;
  const S = parallelSign(D, Dir);
  const m =
    S * Math.tan(toRadians(amt)) * Math.cos(toRadians(alpha)) * Math.cos(toRadians(I));
  const result = crossSectionTraces({ Xp: start.Xp, Hp: start.Hp, m, geom });
  const alert =
    wallAlert(result.wall) || (Math.abs(m) < 1e-9 ? levelCoincidence(start.Hp, geom) : null);
  return { ...result, S, m, alert, ...start };
}

/**
 * SUBROUTINE-ALPHA0-INCLINED — strike parallel to the tunnel, I != 0.
 *
 * The plane's elevation is constant along the drive, while the invert of a
 * tunnel falling towards D drops by tan I per metre of chainage, so the
 * plane's height above the invert at any perimeter point (X, Z) is
 *
 *   Z = Hp + m (X - Xp) + (RD - RD0) tan I,   m = S tan(Amt) / cos I   (AI-3)
 *
 * which fixes the chainage at which the trace passes that point:
 *
 *   RD = RD0 + [Z - Hp - m (X - Xp)] / tan I
 *
 * Signed inclination is positive for a tunnel falling towards D, negative
 * for a rising tunnel. The same equation covers both directions.
 *
 * The trace is one continuous curve from right invert to left invert:
 * straight on the walls (RD is affine in Z), curved over the crown.
 *
 * Returns:
 *   points     — perimeter polyline in log order, each { surface, X, Z, Ylog, RD }
 *   crossings  — RD at which the trace passes P1, P2, P5, P4, P3
 *   m, tanI
 */
export function alphaZeroInclined({ Xp, Hp, S, amt, I, RD0, geom, crownSteps = 90 }) {
  const { W, WS, HW, H, R, HWn, Arc, ArcFull, Theta } = geom;
  const tanI = Math.tan(toRadians(I));
  const m = ((S || 0) * Math.tan(toRadians(amt))) / Math.cos(toRadians(I));
  const a = W / 2;
  const b = wallTaper(geom);
  const HL = wallLength(geom);
  const rdAt = (X, Z) => RD0 + (Z - Hp - m * (X - Xp)) / tanI;

  const points = [];
  const push = (surface, X, Z, Ylog) => points.push({ surface, X, Z, Ylog, RD: rdAt(X, Z) });

  // AI-5 — right wall, invert edge up to the SPL
  push("rightwall", a, 0, 0);
  push("rightwall", a + b * HW, HW, HL);

  // AI-6 / AI-7 — crown, sampled in n so the curve is honest
  const span = 90 - Theta;
  if (span > 0) {
    for (let k = 0; k <= crownSteps; k++) {
      const n = Theta + (span * k) / crownSteps;
      const X = R * Math.cos(toRadians(n));
      const Z = HWn + R * Math.sin(toRadians(n));
      push("crown", X, Z, HL + Arc * ((n - Theta) / span));
    }
    for (let k = crownSteps; k >= 0; k--) {
      const n = Theta + (span * k) / crownSteps;
      const X = -R * Math.cos(toRadians(n));
      const Z = HWn + R * Math.sin(toRadians(n));
      push("crown", X, Z, HL + ArcFull - Arc * ((n - Theta) / span));
    }
  }

  // AI-8 — left wall, SPL down to the invert edge
  push("leftwall", -a - b * HW, HW, HL + ArcFull);
  push("leftwall", -a, 0, 2 * HL + ArcFull);

  const crossings = {
    P1: rdAt(a, 0),
    P2: rdAt(WS / 2, HW),
    P5: rdAt(0, H),
    P4: rdAt(-WS / 2, HW),
    P3: rdAt(-a, 0),
  };

  return { points, crossings, m, tanI };
}

/** Chainage extent of a perimeter polyline. */
export function polylineExtent(points) {
  const rds = points.map((p) => p.RD);
  return { min: Math.min(...rds), max: Math.max(...rds) };
}

/**
 * Chainage extent of the part of a perimeter polyline above the SPL (the
 * crown), or null when the polyline never reaches it.
 */
export function crownExtent(points) {
  const crown = points.filter((p) => p.surface === "crown");
  return crown.length ? polylineExtent(crown) : null;
}

/**
 * Length of a perimeter polyline in metres, optionally only the part between
 * two chainages — segments are cut at the limits, not dropped.
 */
export function polylineLength(points, startRD = -Infinity, endRD = Infinity) {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    let { RD: r0, Ylog: y0 } = points[i - 1];
    let { RD: r1, Ylog: y1 } = points[i];
    if (r0 > r1) [r0, y0, r1, y1] = [r1, y1, r0, y0];
    if (r1 < startRD || r0 > endRD) continue;
    if (r1 === r0) {
      total += Math.abs(y1 - y0);
      continue;
    }
    const slope = (y1 - y0) / (r1 - r0);
    const c0 = Math.max(r0, startRD);
    const c1 = Math.min(r1, endRD);
    const d = c1 - c0;
    total += Math.sqrt(d * d + (slope * d) * (slope * d));
  }
  return total;
}
